import AiDataTables from 'dashboard/api/aiDataTables';

// Tabelas de preço da IA comercial. `current` carrega o DRAFT completo
// (fields + rows) da tabela aberta no detalhe; `publishErrors` é o retorno
// 422 do publish ({geral: [], linhas: {rowId: []}}) — fica de pé até um
// publish passar ou a tabela trocar (fail loud persistente, não toast).

const types = {
  SET_UI_FLAG: 'SET_UI_FLAG',
  SET_LIST: 'SET_LIST',
  SET_CURRENT: 'SET_CURRENT',
  UPSERT_IN_LIST: 'UPSERT_IN_LIST',
  REMOVE_FROM_LIST: 'REMOVE_FROM_LIST',
  UPSERT_FIELD: 'UPSERT_FIELD',
  REMOVE_FIELD: 'REMOVE_FIELD',
  UPSERT_ROW: 'UPSERT_ROW',
  REMOVE_ROW: 'REMOVE_ROW',
  MARK_DRAFT_CHANGED: 'MARK_DRAFT_CHANGED',
  SET_PUBLISH_ERRORS: 'SET_PUBLISH_ERRORS',
  SET_ERROR: 'SET_ERROR',
};

const apiErrorMessage = error => {
  const data = error.response?.data;
  // destroy bloqueado responde { errors: [...] } (qual relação/fluxo trava) —
  // sem ler o array, o toast mostraria o texto genérico do axios e o operador
  // não saberia o que remover.
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.join('; ');
  }
  return data?.message || data?.error || error.message;
};

const state = {
  list: [],
  current: null,
  publishErrors: null,
  error: null,
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    saving: false,
    savingField: false,
    savingRow: false,
    publishing: false,
    destroying: false,
  },
};

const getters = {
  getList: $state => $state.list,
  getCurrent: $state => $state.current,
  getPublishErrors: $state => $state.publishErrors,
  getError: $state => $state.error,
  getUIFlags: $state => $state.uiFlags,
};

const wrap = async (commit, flag, fn) => {
  commit(types.SET_UI_FLAG, { [flag]: true });
  try {
    return await fn();
  } catch (error) {
    commit(types.SET_ERROR, apiErrorMessage(error));
    throw error;
  } finally {
    commit(types.SET_UI_FLAG, { [flag]: false });
  }
};

const actions = {
  async fetchList({ commit }) {
    await wrap(commit, 'fetchingList', async () => {
      const { data } = await AiDataTables.get();
      commit(types.SET_LIST, data.payload);
    });
  },

  async fetchItem({ commit }, id) {
    await wrap(commit, 'fetchingItem', async () => {
      const { data } = await AiDataTables.show(id);
      commit(types.SET_CURRENT, data);
    });
  },

  async create({ commit }, payload) {
    return wrap(commit, 'saving', async () => {
      const { data } = await AiDataTables.create(payload);
      commit(types.SET_CURRENT, data);
      commit(types.UPSERT_IN_LIST, data);
      return data;
    });
  },

  async update({ commit }, { id, ...payload }) {
    await wrap(commit, 'saving', async () => {
      const { data } = await AiDataTables.update(id, payload);
      commit(types.SET_CURRENT, data);
      commit(types.UPSERT_IN_LIST, data);
    });
  },

  async destroy({ commit }, id) {
    await wrap(commit, 'destroying', async () => {
      await AiDataTables.delete(id);
      commit(types.REMOVE_FROM_LIST, id);
      commit(types.SET_CURRENT, null);
    });
  },

  async publish({ commit }, id) {
    await wrap(commit, 'publishing', async () => {
      try {
        const { data } = await AiDataTables.publish(id);
        commit(types.SET_CURRENT, data);
        commit(types.UPSERT_IN_LIST, data);
        commit(types.SET_PUBLISH_ERRORS, null);
      } catch (error) {
        // 422 estruturado do PublishService: erros gerais + por linha.
        const structured = error.response?.data?.errors;
        if (error.response?.status === 422 && structured) {
          commit(types.SET_PUBLISH_ERRORS, structured);
        }
        throw error;
      }
    });
  },

  clearPublishErrors({ commit }) {
    commit(types.SET_PUBLISH_ERRORS, null);
  },

  async createField({ commit }, { tableId, ...payload }) {
    await wrap(commit, 'savingField', async () => {
      const { data } = await AiDataTables.createField(tableId, payload);
      commit(types.UPSERT_FIELD, data);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  async updateField({ commit }, { tableId, fieldId, ...payload }) {
    await wrap(commit, 'savingField', async () => {
      const { data } = await AiDataTables.updateField(
        tableId,
        fieldId,
        payload
      );
      commit(types.UPSERT_FIELD, data);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  async destroyField({ commit }, { tableId, fieldId }) {
    await wrap(commit, 'savingField', async () => {
      await AiDataTables.destroyField(tableId, fieldId);
      commit(types.REMOVE_FIELD, fieldId);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  async createRow({ commit }, { tableId, ...payload }) {
    await wrap(commit, 'savingRow', async () => {
      const { data } = await AiDataTables.createRow(tableId, payload);
      commit(types.UPSERT_ROW, data);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  async updateRow({ commit }, { tableId, rowId, ...payload }) {
    await wrap(commit, 'savingRow', async () => {
      const { data } = await AiDataTables.updateRow(tableId, rowId, payload);
      commit(types.UPSERT_ROW, data);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  async destroyRow({ commit }, { tableId, rowId }) {
    await wrap(commit, 'savingRow', async () => {
      await AiDataTables.destroyRow(tableId, rowId);
      commit(types.REMOVE_ROW, rowId);
      commit(types.MARK_DRAFT_CHANGED);
    });
  },

  reset({ commit }) {
    commit(types.SET_CURRENT, null);
    commit(types.SET_PUBLISH_ERRORS, null);
    commit(types.SET_ERROR, null);
  },
};

const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_LIST]($state, list) {
    $state.list = [...list];
  },
  [types.SET_CURRENT]($state, table) {
    $state.current = table ? { ...table } : null;
    if (table) $state.publishErrors = null;
  },
  [types.UPSERT_IN_LIST]($state, table) {
    const summary = { ...table, fields: undefined, rows: undefined };
    const exists = $state.list.some(item => item.id === table.id);
    $state.list = exists
      ? $state.list.map(item => (item.id === table.id ? summary : item))
      : [...$state.list, summary];
  },
  [types.REMOVE_FROM_LIST]($state, id) {
    $state.list = $state.list.filter(item => item.id !== id);
  },
  [types.UPSERT_FIELD]($state, field) {
    if (!$state.current) return;
    const fields = $state.current.fields || [];
    const exists = fields.some(item => item.id === field.id);
    $state.current = {
      ...$state.current,
      fields: exists
        ? fields.map(item => (item.id === field.id ? field : item))
        : [...fields, field],
    };
  },
  [types.REMOVE_FIELD]($state, fieldId) {
    if (!$state.current) return;
    $state.current = {
      ...$state.current,
      fields: ($state.current.fields || []).filter(item => item.id !== fieldId),
    };
  },
  [types.UPSERT_ROW]($state, row) {
    if (!$state.current) return;
    const rows = $state.current.rows || [];
    const exists = rows.some(item => item.id === row.id);
    $state.current = {
      ...$state.current,
      rows: exists
        ? rows.map(item => (item.id === row.id ? row : item))
        : [...rows, row],
    };
  },
  [types.REMOVE_ROW]($state, rowId) {
    if (!$state.current) return;
    $state.current = {
      ...$state.current,
      rows: ($state.current.rows || []).filter(item => item.id !== rowId),
    };
  },
  // Espelho local do badge do backend: mexeu no draft de tabela publicada,
  // o "rascunho alterado" acende sem refetch.
  [types.MARK_DRAFT_CHANGED]($state) {
    if (!$state.current?.published) return;
    $state.current = { ...$state.current, draft_changed: true };
  },
  [types.SET_PUBLISH_ERRORS]($state, errors) {
    $state.publishErrors = errors ? { ...errors } : null;
  },
  [types.SET_ERROR]($state, error) {
    $state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
